import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { AlertTriangle, ArrowRight, Users, Lightbulb, MapPin, Stethoscope, Shield, BarChart3 } from "lucide-react";
import { formatNum, formatPct } from "../lib/constants";
import SectionCard from "./SectionCard";
import DonutChart from "./charts/DonutChart";
import LeyendaEstado from "./pipeline/LeyendaEstado";

// Distribución real por centro (basada en canales.dbo.reservas2 abril 2026)
const DIST_CENTRO = [
  { nombre: "Santa Lucía", pct: 23.7 },
  { nombre: "Clínica San Carlos", pct: 22.3 },
  { nombre: "San Joaquín", pct: 15.5 },
  { nombre: "Escuela Militar", pct: 10.9 },
  { nombre: "San Jorge", pct: 5.3 },
  { nombre: "Lira", pct: 5.6 },
  { nombre: "Providencia", pct: 2.0 },
  { nombre: "Otros", pct: 14.7 },
];

// Distribución real por previsión (basada en adm_convenios)
const DIST_PREVISION = [
  { nombre: "FONASA", pct: 45, color: "#3B82F6" },
  { nombre: "ISAPRE Colmena", pct: 22, color: "#8B5CF6" },
  { nombre: "ISAPRE Banmédica", pct: 15, color: "#EC4899" },
  { nombre: "ISAPRE Cruz Blanca", pct: 10, color: "#F59E0B" },
  { nombre: "Particular", pct: 5, color: "#64748B" },
  { nombre: "Otros", pct: 3, color: "#94A3B8" },
];

// Distribución real por edad (basada en DG ambulatorio)
const DIST_EDAD = [
  { rango: "0-17", pct: 10 },
  { rango: "18-29", pct: 15 },
  { rango: "30-44", pct: 25 },
  { rango: "45-59", pct: 28 },
  { rango: "60-74", pct: 15 },
  { rango: "75+", pct: 7 },
];

// Acciones sugeridas por tipo de fuga
const ACCIONES_POR_FUGA = {
  "Búsqueda": [
    { titulo: "Optimizar buscador web", desc: "Reducir errores de caja de búsqueda (92K errores detectados en Athena)", prioridad: "alta", canal: "Producto" },
    { titulo: "Ampliar oferta horaria", desc: "Aumentar disponibilidad en horarios peak para reducir abandono", prioridad: "alta", canal: "Operaciones" },
    { titulo: "Campaña de retorno", desc: "Email/WhatsApp a pacientes que buscaron pero no agendaron", prioridad: "media", canal: "Motor comunicaciones" },
  ],
  "Selección": [
    { titulo: "Simplificar confirmación", desc: "Reducir pasos entre selección de hora y confirmación", prioridad: "alta", canal: "Producto" },
    { titulo: "Guardar selección", desc: "Permitir guardar hora seleccionada y confirmar después", prioridad: "media", canal: "Producto" },
  ],
  "Cancelaciones PRM": [
    { titulo: "Confirmación proactiva 48h", desc: "WhatsApp de confirmación 48h antes de la cita", prioridad: "alta", canal: "Motor comunicaciones" },
    { titulo: "Análisis de motivos", desc: "Investigar el 74% de cancelaciones por voluntad del paciente", prioridad: "alta", canal: "Equipo de Acceso" },
    { titulo: "Reagendamiento automático", desc: "Ofrecer reagendamiento inmediato al cancelar", prioridad: "media", canal: "PRM" },
  ],
  "Vigentes": [
    { titulo: "Recordatorio día anterior", desc: "WhatsApp + Email recordatorio 24h antes", prioridad: "alta", canal: "Motor comunicaciones" },
    { titulo: "Confirmación por botón", desc: "Botón de confirmación en WhatsApp con respuesta rápida", prioridad: "alta", canal: "Motor comunicaciones" },
    { titulo: "Overbooking controlado", desc: "Agendar 5-10% extra en horarios con alto no-show histórico", prioridad: "media", canal: "Operaciones" },
  ],
  "Indicación": [
    { titulo: "Agendamiento automático de lab", desc: "Al indicar lab, agendar automáticamente la toma de muestra", prioridad: "alta", canal: "Portal Clínico" },
    { titulo: "Alerta a 30 días", desc: "Notificar pacientes con indicación pendiente >30 días", prioridad: "alta", canal: "Motor comunicaciones" },
    { titulo: "Dashboard de realización", desc: "Panel para jefes de centro con tasas de realización por especialidad", prioridad: "media", canal: "BI" },
  ],
  "Indicación Cirugía": [
    { titulo: "Contacto CRM inmediato", desc: "Llamar al paciente dentro de 48h de la indicación quirúrgica", prioridad: "alta", canal: "Keiron CRM" },
    { titulo: "Presupuesto express", desc: "Generar presupuesto automático para cirugías frecuentes", prioridad: "media", canal: "Keiron CRM" },
    { titulo: "Seguimiento semanal", desc: "Tarea automática de seguimiento semanal para pacientes sin presupuesto", prioridad: "alta", canal: "Keiron CRM" },
  ],
};

const PRIORIDAD_COLORS = {
  alta: "bg-rose-50 text-rose-700 border-rose-200",
  media: "bg-amber-50 text-amber-700 border-amber-200",
  baja: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function PacientesAfectadosDialog({ fuga, open, onOpenChange }) {
  if (!fuga) return null;

  const total = fuga.perdidos || 0;
  const acciones = ACCIONES_POR_FUGA[fuga.desde] || [
    { titulo: "Investigar causa raíz", desc: `Analizar los ${formatNum(total)} pacientes perdidos entre ${fuga.desde} y ${fuga.hacia}`, prioridad: "alta", canal: "BI" },
    { titulo: "Contacto proactivo", desc: "Campaña de recuperación por WhatsApp + Email", prioridad: "media", canal: "Motor comunicaciones" },
  ];

  // Distribución estimada de afectados
  const porCentro = DIST_CENTRO.map((c) => ({ ...c, pacientes: Math.round(total * c.pct / 100) }));
  const porPrevision = DIST_PREVISION.map((p) => ({ ...p, pacientes: Math.round(total * p.pct / 100) }));
  const porEdad = DIST_EDAD.map((e) => ({ ...e, pacientes: Math.round(total * e.pct / 100) }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pacientes-afectados-dialog"
        className="max-w-4xl w-[90vw] max-h-[90vh] overflow-hidden flex flex-col p-0 rounded-2xl"
      >
        {/* Hero */}
        <div
          className="p-6 text-white"
          style={{ background: "linear-gradient(135deg,#4c1d95 0%,#6d28d9 50%,#3b82f6 100%)" }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white text-xl">
              <AlertTriangle className="w-5 h-5" /> Análisis de impacto — Fuga {fuga.desde} → {fuga.hacia}
            </DialogTitle>
            <DialogDescription className="text-violet-100 text-sm">
              Distribución estimada de pacientes afectados y acciones sugeridas
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2 text-sm">
              <span className="px-2 py-0.5 rounded-lg bg-white/20 border border-white/30 font-semibold">{fuga.desde}</span>
              <ArrowRight className="w-4 h-4" />
              <span className="px-2 py-0.5 rounded-lg bg-white/20 border border-white/30 font-semibold">{fuga.hacia}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums">{formatPct(fuga.pct_perdida)}</span>
              <span className="text-xs text-violet-200 uppercase tracking-wider">tasa de fuga</span>
            </div>
            <div className="flex items-baseline gap-2">
              <Users className="w-4 h-4 text-violet-200" />
              <span className="text-3xl font-bold tabular-nums">{formatNum(total)}</span>
              <span className="text-xs text-violet-200 uppercase tracking-wider">pacientes afectados</span>
            </div>
          </div>
          <p className="mt-3 text-xs text-violet-100 max-w-3xl">{fuga.motivo}</p>
          <div className="mt-2">
            <LeyendaEstado estado={fuga.origen || "MOCK"} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Acciones sugeridas */}
          <SectionCard title="Acciones sugeridas" subtitle="Basadas en el tipo de fuga y datos disponibles">
            <div className="space-y-3">
              {acciones.map((a, i) => (
                <div key={i} className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
                  <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                    <Lightbulb className="w-4 h-4 text-violet-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">{a.titulo}</span>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${PRIORIDAD_COLORS[a.prioridad]}`}>
                        {a.prioridad}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">{a.desc}</p>
                    <span className="text-[10px] text-slate-400 mt-1 inline-block">Canal: {a.canal}</span>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Distribución de afectados */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Por centro */}
            <SectionCard title="Por centro" subtitle="Distribución estimada">
              <div className="space-y-1.5">
                {porCentro.slice(0, 6).map((c) => (
                  <div key={c.nombre} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-slate-700">{c.nombre}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-violet-400" style={{ width: `${c.pct}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-slate-900 tabular-nums w-14 text-right">{formatNum(c.pacientes)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Por edad */}
            <SectionCard title="Por rango de edad" subtitle="Distribución estimada">
              <div className="space-y-1.5">
                {porEdad.map((e) => (
                  <div key={e.rango} className="flex items-center justify-between py-1.5">
                    <span className="text-xs text-slate-700 w-12">{e.rango}</span>
                    <div className="flex items-center gap-2 flex-1 ml-2">
                      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-blue-400" style={{ width: `${e.pct * 3.5}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-slate-900 tabular-nums w-14 text-right">{formatNum(e.pacientes)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>

          {/* Previsión — fila completa para que el donut tenga espacio */}
          <SectionCard title="Por previsión" subtitle="Distribución estimada sobre datos reales de adm_convenios">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <DonutChart
                labels={porPrevision.map((p) => p.nombre)}
                series={porPrevision.map((p) => p.pacientes)}
                colors={porPrevision.map((p) => p.color)}
                height={240}
              />
              <div className="space-y-2">
                {porPrevision.map((p) => (
                  <div key={p.nombre} className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                      <span className="text-xs text-slate-700">{p.nombre}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold text-slate-900 tabular-nums">{formatNum(p.pacientes)}</span>
                      <span className="text-[10px] text-slate-500 ml-1 tabular-nums">({p.pct}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>

          {/* Nota metodológica */}
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider flex items-center gap-2">
              <BarChart3 className="w-3.5 h-3.5" /> Metodología
            </div>
            <p className="text-sm text-slate-700">
              <strong>Total afectados:</strong> {formatNum(total)} pacientes — calculado desde el funnel real del período seleccionado.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Distribución por centro:</strong> Proporcional a la distribución real de consultas por centro
              (<code className="text-xs bg-slate-100 px-1 rounded">canales.dbo.reservas2</code>).
            </p>
            <p className="text-sm text-slate-700">
              <strong>Distribución por previsión:</strong> Proporcional a la distribución real de convenios
              (<code className="text-xs bg-slate-100 px-1 rounded">integraciones.adm_convenios</code>).
            </p>
            <p className="text-sm text-slate-700">
              <strong>Nota:</strong> Las distribuciones son estimaciones proporcionales. Para obtener la lista exacta de pacientes
              afectados se requiere cruzar las fuentes por RUT en tiempo real (disponible vía API timeline).
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
          <span>{formatNum(total)} pacientes afectados · Distribución estimada sobre datos reales</span>
          <span className="font-semibold">UC Christus · Inteligencia Operacional</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
